import { getLocale } from './locale'

/**
 * Helpers for number fields that show the user's own separators while they
 * type. The field holds a *draft*: a plain string like `-1234.5` (digits, an
 * optional leading minus, an optional `.` decimal point) that survives a
 * half-typed value such as `12.` and that `Number()` reads as-is. Only what the
 * user sees is localised — "1,234.5", "1 234,5", "1.234,5".
 */

export interface DraftRules {
  allowNegative: boolean
  allowDecimal: boolean
}

interface Separators {
  decimal: string
  /** Characters accepted as the decimal point — the locale's, plus `.` where it is free. */
  decimalInputs: string[]
  integers: Intl.NumberFormat
}

const separatorCache = new Map<string, Separators>()

function getSeparators(): Separators {
  const locale = getLocale()
  let cached = separatorCache.get(locale)
  if (!cached) {
    const parts = new Intl.NumberFormat(locale, { numberingSystem: 'latn' }).formatToParts(
      1234567.5
    )
    const group = parts.find((p) => p.type === 'group')?.value ?? ','
    const decimal = parts.find((p) => p.type === 'decimal')?.value ?? '.'
    cached = {
      decimal,
      // A Swede reaching for the full stop means a decimal point; a German means grouping.
      decimalInputs: decimal === '.' || group === '.' ? [decimal] : [decimal, '.'],
      integers: new Intl.NumberFormat(locale, {
        numberingSystem: 'latn',
        maximumFractionDigits: 0,
      }),
    }
    separatorCache.set(locale, cached)
  }
  return cached
}

/** Whether a character counts towards the value — anything but grouping and stray text. */
function isSignificant(ch: string, decimalInputs: string[]): boolean {
  return (ch >= '0' && ch <= '9') || ch === '-' || decimalInputs.includes(ch)
}

/** Reads what the user typed or pasted back into a draft, dropping anything the rules forbid. */
export function parseNumberDraft(text: string, rules: DraftRules): string {
  const { decimalInputs } = getSeparators()
  let negative = false
  let int = ''
  let frac: string | null = null
  for (const ch of text) {
    if (ch >= '0' && ch <= '9') {
      if (frac === null) int += ch
      else frac += ch
    } else if (ch === '-' || ch === '−') {
      if (rules.allowNegative && !int && frac === null) negative = true
    } else if (rules.allowDecimal && frac === null && decimalInputs.includes(ch)) {
      frac = ''
    }
  }
  int = int.replace(/^0+(?=\d)/, '')
  if (frac !== null && !int) int = '0'
  return (negative ? '-' : '') + int + (frac !== null ? `.${frac}` : '')
}

/** A draft as the user should see it, with their group and decimal separators. */
export function formatNumberDraft(draft: string): string {
  const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(draft)
  if (!match) return draft
  const [, sign, int, frac] = match
  const { decimal, integers } = getSeparators()
  const grouped = int ? integers.format(BigInt(int)) : ''
  return sign + grouped + (frac !== undefined ? decimal + frac : '')
}

/**
 * Where the caret belongs in `formatted` so the same number of digits follow
 * it as followed it in `raw`. Counting from the right keeps it steady while
 * separators come and go to its left.
 */
export function mapCaret(raw: string, caret: number, formatted: string): number {
  const { decimalInputs } = getSeparators()
  let after = 0
  for (const ch of raw.slice(caret)) if (isSignificant(ch, decimalInputs)) after++
  let pos = formatted.length
  while (after > 0 && pos > 0) {
    pos--
    if (isSignificant(formatted[pos], decimalInputs)) after--
  }
  // Stay against the digit just typed rather than after a separator.
  while (pos > 0 && !isSignificant(formatted[pos - 1], decimalInputs)) pos--
  return pos
}

/** Whether a character in the formatted value is a separator the caret should skip over. */
export function isGroupingChar(ch: string | undefined): boolean {
  return ch !== undefined && !isSignificant(ch, getSeparators().decimalInputs)
}
